/**
 * Props for {@link Pagination} when using offset/limit against a known `total` row count
 * (matches the pattern in list UIs that store offset in React state).
 *
 * Requires a **positive finite** `limit`. If `limit` is zero, negative, `NaN`, or non-finite,
 * navigation handlers are omitted and `from`/`to` fall back to a full-range display so
 * `Math.ceil(total / limit)` is never evaluated with an invalid divisor.
 */
export function buildOffsetPaginationProps(
  total: number,
  limit: number,
  offset: number,
  setOffset: (next: number) => void,
) {
  if (!Number.isFinite(limit) || limit <= 0) {
    return {
      total,
      from: total > 0 ? 1 : 0,
      to: total,
      onFirstClick: undefined,
      onPreviousClick: undefined,
      onNextClick: undefined,
      onLastClick: undefined,
    };
  }

  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + limit < total;
  const lastPageOffset = (Math.ceil(total / limit) - 1) * limit;
  return {
    total,
    from: offset + 1,
    to: Math.min(offset + limit, total),
    onFirstClick: hasPreviousPage ? () => setOffset(0) : undefined,
    onPreviousClick: hasPreviousPage ? () => setOffset(Math.max(0, offset - limit)) : undefined,
    onNextClick: hasNextPage ? () => setOffset(offset + limit) : undefined,
    onLastClick: hasNextPage ? () => setOffset(lastPageOffset) : undefined,
  };
}
